import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Tooltip,
} from '@chakra-ui/react';
import Icons from '../../../Icons';
import {
  Row,
  NumberField,
  errorStyle,
  hintStyle,
  selectStyle,
} from './SettingControls';
import { useEditor } from 'components/Canvas';
import useTemplateEditorContext from 'scenes/templateEditor/hooks/useTemplateEditorContext';
import {
  BOX_MIN_FONT_SIZE,
  boxRole,
  shrinkMinFontSize,
  shrinkModeOf,
  withReservedHeight,
} from 'helpers/textBox';
import { stackIdOf } from 'helpers/stacks';

// the button's colour while the text shrinks or its box height is fixed
const ACTIVE_COLOR = '#b7791f';

const tooltipOf = (mode, role) =>
  ({
    box: 'Text box: shrinks to fit its box',
    stack: 'Text box: shrinks when the stack is full',
  }[mode] ||
  {
    reserve: 'Text box: reserved height',
    fixed: 'Text box: fixed height',
  }[role] ||
  'Text box');

/**
 * The text box control for the selected text: how the text shrinks when it
 * renders too long (never, to fit its own box, or, in a stack, when the stack
 * runs out of room) with its minimum font size, and in a stack, a reserved
 * height before the text pushes. A fixed box shows top and bottom handles for
 * dragging it taller or shorter than its text. Every change saves at once,
 * typed fields when left or on Enter.
 */
function TextBox() {
  const editor = useEditor();
  const { activeObject, textBoxMenuOpen, setTextBoxMenuOpen } =
    useTemplateEditorContext();
  // shared with the context menu, which opens this popover too
  const isOpen = textBoxMenuOpen;
  const onOpen = () => setTextBoxMenuOpen(true);
  const onClose = () => setTextBoxMenuOpen(false);

  // re-read the settings after any change, including undo and a handle drag
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setRevision(r => r + 1);
    editor.on('textbox:changed', bump);
    editor.on('stacks:changed', bump);
    editor.on('history:changed', bump);
    return () => {
      editor.off('textbox:changed', bump);
      editor.off('stacks:changed', bump);
      editor.off('history:changed', bump);
    };
  }, [editor]);

  useEffect(
    () => setTextBoxMenuOpen(false),
    [activeObject, setTextBoxMenuOpen]
  );

  const handler = editor?.handlers?.textBoxHandler;
  if (!handler || !handler.isText(activeObject)) return null;
  const mode = shrinkModeOf(activeObject);
  const role = boxRole(activeObject);

  return (
    <Popover
      placement="bottom-end"
      isLazy
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Tooltip placement="bottom" label={tooltipOf(mode, role)}>
        <Box display="inline-block">
          <PopoverTrigger>
            <IconButton
              name="textBox"
              color={mode !== 'off' || role ? ACTIVE_COLOR : '#000000'}
              borderRadius="0"
              variant="ghost"
              size="sm"
              icon={<Icons.TextBox size={24} />}
            />
          </PopoverTrigger>
        </Box>
      </Tooltip>

      <Portal>
        <PopoverContent
          borderRadius={0}
          width="360px"
          sx={{ padding: '1rem 1.25rem' }}
        >
          <TextBoxSettings
            object={activeObject}
            handler={handler}
            revision={revision}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function TextBoxSettings({ object, handler, revision }) {
  const stackId = stackIdOf(object);
  const inStack = stackId !== null;
  const mode = shrinkModeOf(object);
  const role = boxRole(object);

  const [minFontSize, setMinFontSize] = useState(
    String(shrinkMinFontSize(object))
  );
  const [errors, setErrors] = useState({});

  // a change from elsewhere (undo, the stack settings) resets the typed field
  useEffect(() => {
    setMinFontSize(String(shrinkMinFontSize(object)));
  }, [object, revision]);

  const report = result =>
    setErrors(result.ok ? {} : result.errors || { box: result.error });

  // the floor carries over when the way of shrinking changes
  const setShrink = nextMode => {
    const raw = String(minFontSize).trim();
    const floor = raw === '' ? BOX_MIN_FONT_SIZE : Number(raw);
    if (!Number.isFinite(floor)) {
      setErrors({ floor: 'The minimum font size must be a number' });
      return;
    }
    report(handler.setShrink(object, nextMode, floor));
  };

  const setFixed = on =>
    report(
      handler.updateSettings(
        object,
        withReservedHeight(handler.settingsOf(object), on)
      )
    );

  const height = handler.boxHeight(object);
  const over = handler.overflowOf(object);
  const drag = 'Drag its top or bottom handle to change it.';
  const taller =
    over > 0 ? ` The text on the canvas is ${over} px taller.` : '';

  const box =
    {
      shrink: `Box height ${height} px. ${drag}`,
      reserve: `Reserved height ${height} px. ${drag}`,
      fixed: `Box height ${height} px, fixed. Outside a stack this does nothing at render.`,
    }[role] || `The box fits its text (${height} px now).`;
  const behaviour = {
    box: inStack
      ? 'Text taller than the box is shrunk to fit, so it does not push the members below it. Text still too tall at the minimum font size pushes them by the rest.'
      : 'Text taller than the box is shrunk to fit. Text still too tall at the minimum font size runs past the bottom.',
    stack:
      'Taller text pushes the members below it down, and is shrunk once the stack would run past the bottom of the card or push a member past its boundary.',
    off: inStack
      ? 'Taller text pushes the members below it down; it is never shrunk.'
      : 'Taller text runs past the bottom over whatever is below it.',
  }[mode];

  return (
    <Box>
      <Box sx={{ fontWeight: 700, fontSize: '14px', paddingBottom: '0.25rem' }}>
        Text box
      </Box>

      <Row
        label="Shrink text"
        tip={`Choose what happens when the real text is too long for its space:\n\n• "Never": the text is left as it is.\n\n• "To fit its box": drag the box to the height you want. Longer text gets smaller until it fits, down to the minimum font size. If it still doesn't fit, it runs past the bottom of the box${
          inStack ? ' and pushes the items below it down' : ''
        }.${
          inStack
            ? `\n\n• "When the stack is full": longer text pushes the items below it down while there's room. It only gets smaller once the stack would reach the bottom of the card or a boundary, and only as much as it needs to.`
            : ''
        }`}
      >
        <Select
          size="sm"
          fontSize="14px"
          borderRadius={0}
          width="180px"
          name="shrinkText"
          sx={selectStyle}
          value={mode}
          onChange={e => setShrink(e.target.value)}
        >
          <option value="off">Never</option>
          <option value="box">To fit its box</option>
          {inStack && <option value="stack">When the stack is full</option>}
        </Select>
      </Row>

      {mode !== 'off' && (
        <Row
          label="Minimum font size (px)"
          tip={`The smallest the text will shrink to. It can't be set below ${BOX_MIN_FONT_SIZE} px (6 pt when printed).`}
          error={
            errors.box_min_font_size || errors.min_font_size || errors.floor
          }
        >
          <NumberField
            name="shrinkMinFontSize"
            value={minFontSize}
            min={BOX_MIN_FONT_SIZE}
            onChange={setMinFontSize}
            onCommit={() => setShrink(mode)}
          />
        </Row>
      )}

      {inStack && mode !== 'box' && (
        <Row
          label="Reserve height"
          tip={
            "Keep a set amount of space for this text: drag the box's top or bottom handle to size it. Text that fits in the space moves nothing. Longer text pushes the items below it down by the extra.\n\nOff: the space is the height of the text as it looks now."
          }
        >
          <Checkbox
            size="md"
            name="reserveHeight"
            // the app styles every label as a column flexbox, which stacks the
            // box above its (empty) label; row keeps it inline
            display="flex"
            flexDirection="row"
            isChecked={role === 'reserve'}
            onChange={e => setFixed(e.target.checked)}
          />
        </Row>
      )}

      {role === 'fixed' && (
        <Row
          label="Fixed height"
          tip="This text kept the fixed height it had in a stack. Outside a stack that has no effect on the printed card. Untick it to let the box fit its text again."
        >
          <Checkbox
            size="md"
            name="fixedHeight"
            display="flex"
            flexDirection="row"
            isChecked
            onChange={e => setFixed(e.target.checked)}
          />
        </Row>
      )}

      <Box sx={{ ...hintStyle, paddingTop: '0.75rem' }} name="textBoxStatus">
        {`${box}${taller} ${behaviour}`}
      </Box>
      {(errors.box || errors.shrink_to_box || errors.fixed_height) && (
        <Box sx={errorStyle}>
          {errors.box || errors.shrink_to_box || errors.fixed_height}
        </Box>
      )}
    </Box>
  );
}

export default TextBox;
